import type { ItemFieldRow } from './item-field-reader';
import type { StatusLayeringMetadata } from './status-layering';
import type { InventoryAbilityGrantMetadata } from './inventory-ability-grants';
import type { CreatureShapeshiftDependencies } from './creature-shapeshift';
import type { VitalityCompilerDependencies } from './vitality-build-context';
import type { FromBaseInventoryMetadata, FromBaseInventoryNode } from './from-base-inventory';
import { compileInventoryTreeFromBase } from './from-base-inventory';
import { createStatusLayerCompiler } from './status-layering-context';
import { createVitalityCompiler } from './vitality-build-context';
import { createCreaturePostInventoryCompiler } from './creature-post-inventory';
import { createInventoryAbilityGrantCompiler } from './inventory-ability-grants';
import { createAbilityConditionEvaluator } from './ability-conditions';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';
import { readInventoryField } from './inventory-raw-readers';
import { createInventoryTemplateIdentityResolver } from './inventory-template-identity';
import { constructDefinitionItemsFromBase } from '../port/k1-compile/from-base/definition-source';
import { integerLayerTokensFromFragments } from './integer-layer-score';
import { scoreCarrierIntegerLayer } from './formula-variable-resolution';
import { compileEncounterWeather, compileUnderwaterWetness } from './inventory-environment';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { phpJsonFloat } from './php-decimal-values';
import { readCreatureStatuses, AuraOwnerGateUnavailable } from './creature-status-reader';
import { buildPosition } from './inventory-initialization';
import { createFromBasePlayerContext, fromBaseClone, fromBaseRecord, fromBaseUuid, fromBaseCatalogWithSources, memoFromBaseRead, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBasePlayerInput, FromBaseRow } from './from-base-context';
import type { FromBasePlayerSkillMetadata } from './from-base-player-skills';
import { constructPlayerThroughTiersFromBase } from './from-base-player-skills';
import { answerConditionContext, ConditionContextUnknown } from './condition-context';
import { fromBaseStatusReferences } from './from-base-status-references';
import { fromBasePilotedObjectReaders } from './piloted-object';

export interface FromBasePlayerStageMetadata extends FromBasePlayerSkillMetadata {
    statusLayering: StatusLayeringMetadata;
    environment: { underwaterId: string; wetId: string };
    speedTypes: readonly { variable_name: string }[];
    stageSources: Readonly<Record<string, FromBaseRow>>;
    stageMissingDefinitionIds?: readonly string[];
    vitality: Pick<VitalityCompilerDependencies, 'statusCatalog' | 'vitalityCatalog' | 'shellItemKeys' | 'shellDropKeys'>;
    shapeshift: CreatureShapeshiftDependencies['metadata'];
    grants: InventoryAbilityGrantMetadata;
    inventoryMetadata?: FromBaseInventoryMetadata;
}

export function createFromBasePlayerStageContext(base: FromBasePlayerInput, metadata: FromBasePlayerStageMetadata, inventory?: readonly FromBaseRow[]) {
    const catalog = fromBaseCatalogWithSources(base.catalog as Record<string, FromBaseRow>, metadata.stageSources) as Record<string, FromBaseRow>;
    const ids = memoFromBaseRead(catalog, 'catalog-reference-ids', () => new Set(Object.keys(catalog).map(key => key.toUpperCase())));
    const augmented = { ...base, catalog, missing_definition_ids: [...new Set([
        ...base.missing_definition_ids ?? [], ...metadata.stageMissingDefinitionIds ?? [],
    ])].filter(id => !ids.has(id.toUpperCase())) };
    const context = createFromBasePlayerContext(augmented, metadata);
    const instances = new Map<string, FromBaseRow>();
    const index = (rows: readonly FromBaseRow[]): void => {
        for (const row of rows) {
            const id = readInventoryField(row, 'inventory_id');
            if (!fromBaseUuid(id)) throw new TypeError('Invalid constructed inventory identity');
            instances.set(id.toUpperCase(), row);
            if (fromBaseRecord(row.inventory_in_container) || Array.isArray(row.inventory_in_container)) index(Object.values(row.inventory_in_container) as FromBaseRow[]);
        }
    };
    if (inventory) index(inventory);
    const resolveInventory = (id: string): ItemFieldRow | null => {
        const row = instances.get(id.toUpperCase());
        if (row) return row;
        throw new FromBaseStageMissing('inventory-construction-grants', `instance-not-constructed:${id}`);
    };
    const common = { ...context.dependencies, ...fromBasePilotedObjectReaders(base, context.definition), resolveInventoryInstance: resolveInventory,
        resolveInventoryIdentity: createInventoryTemplateIdentityResolver(id => {
            const raw = context.rawDefinition(id); return raw ? constructDefinitionItemsFromBase(raw) : null;
        }) };
    const conditions = createAbilityConditionEvaluator({ ...common, equipmentSlots: metadata.equipmentSlots,
        vitalityStatusIds: metadata.vitalityStatusIds, skillEntries: metadata.skills,
        conditionSource: (kind, args) => {
            try {
                return answerConditionContext(kind, args, { encounter: base.encounter_context ?? null,
                    campaign: base.encounter_context?.campaign_arr, value: context.readers.layerValue,
                    entries: (row, key) => Object.values(context.readers.layerEntries(row, key)),
                    abilityTypes: metadata.abilityTypes, equipmentSlots: metadata.equipmentSlots });
            } catch (error) {
                if (error instanceof ConditionContextUnknown) throw new FromBaseStageMissing('player-condition-context', `${error.kind}:${error.why}`);
                throw error;
            }
        } });
    const vitality = createVitalityCompiler({ ...common, ...metadata.vitality, resolveShell: context.definition });
    const formula = { classes: metadata.preliminary.classes, essences: metadata.preliminary.essences };
    const casterTeam = (raw: unknown): unknown => {
        if (!Array.isArray(base.status_owners)) throw new FromBaseInputMissing('status-owner-rows', base.entity_id);
        const id = phpJsonString(raw), owner = base.status_owners.find(row => phpJsonString(context.readers.layerValue(row, 'encounter_creature_id')).toUpperCase() === id.toUpperCase());
        return owner ? context.readers.layerValue(owner, 'team') : null;
    };
    return { ...context, common, conditions, vitality, formula, casterTeam };
}

/** Post-tiers status and environment window. BASE stays uncomputed; all working
 * vitality and status projections are produced locally. No consequence is saved. */
export function constructPlayerStatusEnvironmentFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerStageMetadata, prefix: FromBaseRow,
    inventory?: (id: string) => ItemFieldRow | null): FromBaseRow {
    const context = createFromBasePlayerStageContext(base, metadata), { readers, definition, rawDefinition, common, conditions, vitality, formula, casterTeam } = context;
    let host = fromBaseClone(prefix);
    const ec = fromBaseRecord(host.encounter_creatures) ? host.encounter_creatures : {};
    const field = (row: ItemFieldRow, key: string) => readers.layerValue(row, key);
    if (!phpJsonTruthy(field(ec, 'encounter_creature_id'))) return host;
    if (!fromBaseRecord(base.statuses) || !Array.isArray(base.statuses.target) || !Array.isArray(base.statuses.caster))
        throw new FromBaseInputMissing('saved-status-buckets', base.entity_id);
    host = buildPosition(vitality.compile(host, ec).row, Object.fromEntries(['x_pos', 'y_pos', 'z_pos'].map(key =>
        [key, field(base.encounter_creatures ?? {}, key)]))) as FromBaseRow;
    if (phpJsonTruthy(field(ec, 'encounter_id')) && !base.encounter_context)
        throw new FromBaseInputMissing('encounter-context', base.entity_id);
    host.encounter_creatures = ec;
    host.derivative_speed_basis = { speed_walking: [scoreCarrierIntegerLayer(host, 'speed_walking', 0, true, formula, readers)] };
    host.pre_status_speed_layer_presence = Object.fromEntries(metadata.speedTypes.map(speed => [speed.variable_name,
        integerLayerTokensFromFragments(Object.values(readers.layerEntries(host, speed.variable_name))).some(token => token.trim() !== '')]));
    const fail = (detail: string): never => { throw new FromBaseStageMissing('status-environment-layering', detail); };
    const status = createStatusLayerCompiler({ ...common, metadata: metadata.statusLayering,
        referenceInputs: fromBaseStatusReferences(base as unknown as FromBaseRow, { rawDefinition, definition, fail, inventory }),
        fetchStatuses: () => fail('saved-status-rows'), itemName: common.resolveItemName,
        campaignContext: () => base.encounter_context && fromBaseRecord(base.encounter_context.campaign_arr) ? base.encounter_context.campaign_arr : null,
        campaignSeconds: () => fail('status-calendar-months'),
        casterTeam,
    });
    // Raw SQL rows are complete; lean omission belongs to the later normalization stage.
    delete host.statuses;
    const savedRows = (mode: string) => (base.statuses![mode] as FromBaseRow[]).map(raw => {
        const row = fromBaseClone(raw);
        // build_status_arr descalars the SQL DECIMAL coordinates/distances before layering.
        for (const key of ['status_origin_point_x', 'status_origin_point_y', 'status_origin_point_z',
            'status_distance_point_x', 'status_distance_point_y', 'status_distance_point_z',
            'status_aoe_distance', 'status_aoe_distance_depth', 'status_aoe_distance_height']) row[key] = phpJsonFloat(field(raw, key) ?? 0);
        return row;
    });
    host = status(host, 'target', savedRows('target')) as FromBaseRow;
    host = status(host, 'caster', savedRows('caster')) as FromBaseRow;
    const providers = createCatalogDefaultProviderReader({ catalogRows: metadata.layerCatalog, nodeItemId: readNodeItemId, resolveCarrierRef: definition });
    const entries = (row: ItemFieldRow, key: string) => Object.values(readers.layerEntries(row, key));
    const environment = { metadata: metadata.environment, queryDefinition: definition, immunityReaders: {
        entries, value: readers.layerValue, providers: (row: ItemFieldRow) => [...providers.providers(row)], definition,
        conditionsMet: (ability: ItemFieldRow) => conditions.check(ability, host),
        isAura: (ability: ItemFieldRow) => ['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target'].every(key => field(ability, key) !== null),
    } };
    for (const axis of ['encounter_weather', 'encounter_tempurature', 'encounter_wind']) {
        const id = field(ec, axis);
        if (!fromBaseUuid(id)) continue;
        host = compileEncounterWeather(host, id, environment) as FromBaseRow;
    }
    return compileUnderwaterWetness(host, phpJsonString(field(ec, 'encounter_environment') ?? ''), { metadata: metadata.environment, queryDefinition: definition }) as FromBaseRow;
}

/** C2b supplies its TS-built tree. Its unfinished producer ledger remains on
 * the enclosing result; a projection never certifies full inventory completion. */
export function compilePlayerInventoryFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerStageMetadata) {
    if (!fromBaseRecord(base.encounter_creatures) || !phpJsonTruthy(createFromBasePlayerContext(base, metadata).readers.layerValue(base.encounter_creatures, 'encounter_creature_id')))
        throw new FromBaseStageMissing('inventory-construction-grants', 'template-equipment-and-natural-grants');
    for (const [kind, ids] of [['enchantment-template', base.missing_enchantment_template_ids], ['monster-template', base.missing_monster_template_ids]] as const)
        if (ids?.length) throw new FromBaseInputMissing(kind, ids[0]!);
    if (!metadata.inventoryMetadata || !base.inventory || !base.enchantment_templates || !base.enchantment_template_enchantments || !base.inventory_encounter_links)
        throw new FromBaseInputMissing('inventory-raw-inputs-metadata', base.entity_id);
    return compileInventoryTreeFromBase({ entity_id: base.entity_id, base_rev: base.base_rev,
        pl_signature: base.pl_signature,
        inventory: base.inventory as FromBaseInventoryNode[], enchantment_templates: base.enchantment_templates,
        enchantment_template_enchantments: base.enchantment_template_enchantments, missing_definition_ids: base.missing_definition_ids,
        possessor: { player_characters: base.player_characters, encounter_creatures: base.encounter_creatures },
        encounter_links: base.inventory_encounter_links, inventory_order: base.inventory_order,
        picture_sources: base.picture_sources, picture_lookup_ids: base.picture_lookup_ids, definition_videos: base.definition_videos,
        inventory_metadata: base.inventory_metadata, metadata: metadata.inventoryMetadata }, base.catalog);
}

export function constructPlayerInventoryGrantsFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerStageMetadata, input: FromBaseRow,
    tree = compilePlayerInventoryFromBase(base, metadata)) {
    if (!tree.inventory) throw new FromBaseStageMissing('inventory-construction-grants', tree.detail ?? tree.missingStages.join(','));
    const context = createFromBasePlayerStageContext(base, metadata, tree.inventory), { common, definition, conditions } = context;
    const grants = createInventoryAbilityGrantCompiler({ ...common, metadata: metadata.grants, queryFullItem: definition,
        conditions: (ability, host, template, defer) => conditions.check(ability, host, template, defer),
        redundant: (_host, _inventory, layer) => {
            if (metadata.grants.inventoryAbilityTypes.includes(layer)) throw new FromBaseStageMissing('inventory-grant-redundancy', layer);
            return false; // Non-ability candidates never enter the grant loop.
        },
    });
    return { row: grants.all({ ...input, inventory: tree.inventory }) as FromBaseRow, inventory: tree.inventory,
        missingStages: tree.missingStages };
}

export function constructPlayerPostInventoryFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerStageMetadata, input: FromBaseRow, inventory: readonly FromBaseRow[]) {
    const { common, definition, readers, formula, casterTeam } = createFromBasePlayerStageContext(base, metadata, inventory);
    // The full PC entry supplies the shared NPC definition finalizer. Partial
    // callers still cannot substitute a raw creature for its completed form.
    if (!metadata.definitionLookup) for (const status of Object.values(readCreatureStatuses(input, 'target', definition))) {
        if (!fromBaseRecord(status)) continue;
        const form = fromBaseRecord(status.status_arr) ? status.status_arr : null;
        if (readers.itemField(form, 'item_type') === 'creature')
            throw new FromBaseStageMissing('size-shapeshift-vitality', `creature-form-finalization:${phpJsonString(readers.itemField(form, 'item_id'))}`);
    }
    const compile = createCreaturePostInventoryCompiler({ resolveInventoryInstance: common.resolveInventoryInstance,
        size: { ...common, metadata: formula, queryFullItem: definition, casterTeam,
            nameFromBlob: (_row, id) => phpJsonString(readers.itemField(definition(id), 'item_name') ?? ''),
            resolveItemIdType: id => phpJsonString(readers.itemField(definition(id), 'item_type') ?? 'unknown') },
        shapeshift: { ...common, metadata: metadata.shapeshift, queryFullItem: id => {
            const row = definition(id);
            if (!metadata.definitionLookup && readers.itemField(row, 'item_type') === 'creature')
                throw new FromBaseStageMissing('size-shapeshift-vitality', `creature-form-finalization:${id}`);
            return row;
        }, applyMonsterTemplate: (_row, id) => { throw new FromBaseStageMissing('size-shapeshift-vitality', `monster-template:${id}`); },
        applyCreatureModelMedia: () => { throw new FromBaseStageMissing('size-shapeshift-vitality', 'form-model-media'); },
        picturesOf: () => { throw new FromBaseStageMissing('size-shapeshift-vitality', 'form-pictures'); } },
        vitality: { ...common, ...metadata.vitality, resolveShell: definition },
    });
    return compile(input);
}

export function constructPlayerThroughPostInventoryFromBase(base: FromBasePlayerInput, metadata: FromBasePlayerStageMetadata) {
    const tiers = constructPlayerThroughTiersFromBase(base, metadata);
    if (tiers.kind !== 'constructed-prefix') return tiers;
    try {
        const status = constructPlayerStatusEnvironmentFromBase(base, metadata, tiers.row);
        const grants = constructPlayerInventoryGrantsFromBase(base, metadata, status);
        const post = constructPlayerPostInventoryFromBase(base, metadata, grants.row, grants.inventory);
        return { ...tiers, row: post.row, consequences: { burn: post.burn },
            completedStages: [...tiers.completedStages, 'status-environment-layering', 'inventory-ability-grant-projection', 'ordinary-size-vitality-weight-projection'],
            missingStages: [...tiers.missingStages.filter(stage => stage !== 'status-environment-layering'), ...grants.missingStages.map(stage => `inventory:${stage}`)] };
    } catch (error) {
        if (error instanceof FromBaseInputMissing) return { kind: 'missing-input' as const, stage: 'player-post-tiers', dependency: error.dependency, id: error.id };
        if (error instanceof FromBaseStageMissing) return { kind: 'missing-stage' as const, stage: error.stage, detail: error.detail };
        if (error instanceof AuraOwnerGateUnavailable) return { kind: 'missing-stage' as const, stage: 'status-environment-layering', detail: error.reason };
        throw error;
    }
}
